import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { X, ShoppingCart, Minus, Plus, Trash2, AlertTriangle, Clock } from "lucide-react";
import {
  useCart,
  hasMixedProducts,
  hasPreorderItems,
  cartNeedsFreightQuote,
  cartTotalWeightKg,
} from "../contexts/CartContext";
import { formatAUD } from "../lib/pricing";
import QtyInput from "./QtyInput";

// Formats a line's display label: product title + variant (if not "Default Title").
function lineLabel(line) {
  const productTitle = line.merchandise?.product?.title || "Item";
  const variantTitle = line.merchandise?.title;
  if (!variantTitle || variantTitle === "Default Title") return productTitle;
  return `${productTitle} — ${variantTitle}`;
}

// Format currency from Shopify's { amount, currencyCode } shape.
function fmtMoney(money) {
  if (!money) return "";
  return formatAUD(Number(money.amount));
}

// Key attributes worth surfacing in the drawer (skip internal housekeeping keys).
const DISPLAY_ATTR_KEYS = ["Finish", "Support angle", "Timber support", "Delivery Type"];

function LineAttributes({ attributes }) {
  if (!attributes?.length) return null;
  const visible = attributes.filter((a) => DISPLAY_ATTR_KEYS.includes(a.key));
  if (!visible.length) return null;
  return (
    <ul className="st-cart-line-attrs">
      {visible.map((a) => (
        <li key={a.key}>
          <span className="st-cart-attr-key">{a.key}:</span> {a.value}
        </li>
      ))}
    </ul>
  );
}

export default function CartDrawer() {
  const {
    isCartOpen,
    closeCart,
    lines,
    subtotal,
    checkoutUrl,
    cartLoading,
    updateLine,
    removeLine,
    cart,
  } = useCart();

  // Trap focus and close on Escape.
  const drawerRef = useRef(null);

  useEffect(() => {
    if (!isCartOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeCart();
    };
    document.addEventListener("keydown", onKey);
    // Prevent body scroll while drawer is open.
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [isCartOpen, closeCart]);

  const mixed = hasMixedProducts(cart);
  const hasPreorder = hasPreorderItems(cart);
  const isEmpty = !lines || lines.length === 0;

  // Past the heaviest order Shopify prices, checkout returns no rate at all and
  // fails as "we don't ship to your address", which reads as an address fault.
  // Catch it here and offer the pallet quote instead. Local pickup isn't
  // affected by the weight bands, so checkout stays live rather than gated.
  const overWeight = cartNeedsFreightQuote(cart);
  const orderWeightKg = cartTotalWeightKg(cart);
  const freightQuoteHref = `/contact?product=${encodeURIComponent(
    `Pallet freight quote (${Math.round(orderWeightKg)} kg order)`
  )}`;

  const handleCheckout = () => {
    if (checkoutUrl) window.location.href = checkoutUrl;
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`st-cart-backdrop${isCartOpen ? " is-open" : ""}`}
        onClick={closeCart}
        aria-hidden="true"
      />

      {/* Drawer panel */}
      <div
        ref={drawerRef}
        className={`st-cart-drawer${isCartOpen ? " is-open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label="Shopping cart"
        data-testid="cart-drawer"
      >
        {/* Header */}
        <div className="st-cart-header">
          <span className="st-cart-title">
            <ShoppingCart size={18} strokeWidth={2.4} />
            Your Cart
            {lines.length > 0 && (
              <span className="st-cart-count">
                {lines.reduce((s, l) => s + l.quantity, 0)} item{lines.reduce((s, l) => s + l.quantity, 0) !== 1 ? "s" : ""}
              </span>
            )}
          </span>
          <button
            type="button"
            className="st-cart-close"
            onClick={closeCart}
            aria-label="Close cart"
            data-testid="cart-close-btn"
          >
            <X size={20} strokeWidth={2.4} />
          </button>
        </div>

        {/* Too heavy for any automatic rate — offer the pallet freight quote. */}
        {overWeight && !isEmpty && (
          <div className="st-cart-warning" data-testid="cart-freight-warning">
            <AlertTriangle size={15} strokeWidth={2.4} />
            <span>
              This order weighs about {Math.round(orderWeightKg)} kg, over the heaviest rate
              checkout can work out. An order this size ships on a pallet, quoted to your
              address.{" "}
              <Link to={freightQuoteHref} onClick={closeCart}>
                Request a pallet freight quote
              </Link>{" "}
              and we'll send you the freight cost. Free pickup at Emerald VIC still checks out
              as normal.
            </span>
          </div>
        )}

        {/* Mixed-cart warning */}
        {mixed && (
          <div className="st-cart-warning" data-testid="cart-mixed-warning">
            <AlertTriangle size={15} strokeWidth={2.4} />
            <span>
              Your cart has both brackets and star pickets. Brackets ship Australia-wide; pickets
              need local pickup or delivery. If your delivery address is outside our local
              delivery area, please{" "}
              <Link to="/contact?product=Mixed%20Cart" onClick={closeCart}>
                request a freight quote
              </Link>{" "}
              before checking out.
            </span>
          </div>
        )}

        {/* Pre-order notice — shown when a pre-order bracket (2.5 mm or 3.0 mm
            supported, or 3.0 mm flat) is in the cart. */}
        {hasPreorder && !isEmpty && (
          <div className="st-cart-preorder" data-testid="cart-preorder-notice">
            <Clock size={15} strokeWidth={2.4} />
            <span>
              <strong>Pre-order:</strong> your cart includes a pre-order bracket. These are currently
              being produced and are estimated to dispatch within 2–3 weeks. Tracking is provided as
              soon as your order leaves our workshop.
            </span>
          </div>
        )}

        {/* Lines */}
        <div className="st-cart-lines">
          {isEmpty ? (
            <div className="st-cart-empty" data-testid="cart-empty">
              <ShoppingCart size={36} strokeWidth={1.5} />
              <p>Your cart is empty.</p>
              <button type="button" className="btn-secondary" onClick={closeCart}>
                Continue Shopping
              </button>
            </div>
          ) : (
            lines.map((line) => (
              <div
                key={line.id}
                className="st-cart-line"
                data-testid={`cart-line-${line.id}`}
              >
                <div className="st-cart-line-info">
                  <span className="st-cart-line-title">{lineLabel(line)}</span>
                  <LineAttributes attributes={line.attributes} />
                  <span className="st-cart-line-price">
                    {fmtMoney(line.cost?.totalAmount)}
                  </span>
                </div>
                <div className="st-cart-line-controls">
                  <button
                    type="button"
                    className="st-cart-qty-btn"
                    aria-label="Decrease quantity"
                    disabled={cartLoading}
                    onClick={() =>
                      line.quantity <= 1
                        ? removeLine(line.id)
                        : updateLine(line.id, line.quantity - 1)
                    }
                    data-testid={`cart-line-decrease-${line.id}`}
                  >
                    <Minus size={13} strokeWidth={2.5} />
                  </button>
                  <QtyInput
                    className="st-cart-qty"
                    value={line.quantity}
                    onChange={(n) => updateLine(line.id, n)}
                    live={false}
                    max={9999}
                    disabled={cartLoading}
                    aria-label="Quantity"
                    data-testid={`cart-line-qty-${line.id}`}
                  />
                  <button
                    type="button"
                    className="st-cart-qty-btn"
                    aria-label="Increase quantity"
                    disabled={cartLoading}
                    onClick={() => updateLine(line.id, line.quantity + 1)}
                    data-testid={`cart-line-increase-${line.id}`}
                  >
                    <Plus size={13} strokeWidth={2.5} />
                  </button>
                  <button
                    type="button"
                    className="st-cart-remove"
                    aria-label="Remove item"
                    disabled={cartLoading}
                    onClick={() => removeLine(line.id)}
                    data-testid={`cart-line-remove-${line.id}`}
                  >
                    <Trash2 size={14} strokeWidth={2.4} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        {!isEmpty && (
          <div className="st-cart-footer">
            <div className="st-cart-subtotal">
              <span>Subtotal</span>
              <strong data-testid="cart-subtotal">{fmtMoney(subtotal)}</strong>
            </div>
            {!overWeight && (
              <p className="st-cart-footer-note">Shipping and taxes calculated at checkout.</p>
            )}
            {overWeight ? (
              <>
                <Link
                  to={freightQuoteHref}
                  className="btn-accent st-wide-btn"
                  onClick={closeCart}
                  data-testid="cart-freight-quote-btn"
                >
                  Request a Pallet Freight Quote
                </Link>
                <button
                  type="button"
                  className="btn-secondary st-wide-btn"
                  onClick={handleCheckout}
                  disabled={cartLoading || !checkoutUrl}
                  data-testid="cart-checkout-btn"
                >
                  Checkout (pickup only)
                </button>
              </>
            ) : (
              <button
                type="button"
                className="btn-accent st-wide-btn"
                onClick={handleCheckout}
                disabled={cartLoading || !checkoutUrl}
                data-testid="cart-checkout-btn"
              >
                Checkout
              </button>
            )}
            <button
              type="button"
              className="btn-secondary st-wide-btn"
              onClick={closeCart}
              data-testid="cart-continue-btn"
            >
              Continue Shopping
            </button>
          </div>
        )}

        {/* Loading overlay */}
        {cartLoading && (
          <div className="st-cart-loading-overlay" aria-busy="true" aria-label="Updating cart">
            <span className="st-cart-spinner" />
          </div>
        )}
      </div>
    </>
  );
}
